import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx.js';
export function RrModal({ open, onClose, title, children, footer, 'aria-label': ariaLabel, size = 'default', }) {
    const dialogRef = useRef(null);
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog)
            return;
        if (open && !dialog.open) {
            dialog.showModal();
        }
        else if (!open && dialog.open) {
            dialog.close();
        }
    }, [open]);
    // Wire the native `close` event so consumers see Esc-press as a
    // close intent.
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog)
            return;
        const handle = () => {
            if (open)
                onClose();
        };
        dialog.addEventListener('close', handle);
        return () => {
            dialog.removeEventListener('close', handle);
        };
    }, [open, onClose]);
    // E2E-R1 #5 (root cause): render the dialog through a portal at document.body
    // instead of in place. In the desktop layout the modal was mounted inside
    // `<main style="overflow-y:auto">` (the app content scroll container); a native
    // <dialog> inside that container could be clipped/mis-layered in Chromium/Brave
    // so its controls weren't hittable — but only at the desktop breakpoint (mobile
    // mounts the body in a different, non-scrolling container, which is why it
    // worked on small screens). Portaling to <body> removes ALL ancestor context so
    // the top-layer modal behaves identically everywhere.
    return createPortal(_jsx("dialog", { ref: dialogRef, "aria-label": ariaLabel, onClick: (e) => {
            // Backdrop click — the target is the dialog itself when the
            // click falls outside the inner content surface.
            if (e.target === dialogRef.current)
                onClose();
        }, className: cx(
        // E2E-R1 #5 fix: a CENTERED, content-sized card on every screen size.
        // `m-auto` (with the native modal's inset) centers it; height is capped
        // at 85vh with the body scrolling internally so the header (X) and footer
        // (actions) are ALWAYS visible. The prior layout used `m-0 w-full h-full`,
        // which pinned the dialog full-height to the top-left — putting the X /
        // Skip / Next controls in the top strip behind the sticky app nav, where
        // they couldn't be clicked.
        'p-0 m-auto overflow-hidden', 'bg-rr-surface text-rr-fg-primary rounded-lg shadow-lg', 'w-[92vw] max-h-[85vh]', size === 'wide' ? 'max-w-[960px]' : 'max-w-[480px]'), children: _jsxs("div", { className: "flex flex-col max-h-[85vh]", children: [_jsxs("header", { className: "flex items-center justify-between px-4 sm:px-6 h-12 border-b border-rr-border-default", children: [_jsx("h2", { className: "text-base font-semibold", children: title }), _jsx("button", { type: "button", onClick: onClose, "aria-label": "Close", className: "p-2 -mr-2 rounded-md text-rr-fg-secondary hover:text-rr-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rr-teal-light", children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "2", "aria-hidden": "true", children: _jsx("path", { d: "M4 4L12 12M12 4L4 12" }) }) })] }), _jsx("div", { className: "flex-1 overflow-y-auto px-4 sm:px-6 py-4", children: children }), footer !== undefined ? (_jsx("footer", { className: "flex items-center justify-end gap-2 px-4 sm:px-6 h-14 border-t border-rr-border-default bg-rr-surface-raised", children: footer })) : null] }) }), document.body);
}
//# sourceMappingURL=RrModal.js.map